/**
 * Voice Library — prompts (uploaded audio + synthesized TTS) catalog.
 *
 * Wire format mirrors the legacy obd_frontend module exactly. The library
 * surfaces three views:
 *   - Voice Files: user's own approved prompts (the everyday library).
 *   - All Prompts: admin queue of pending prompts to approve / reject.
 *   - TTS List: history of TTS synthesis attempts (some finalized, some
 *     pending finalize / delete).
 */

/** Approval status values that come back on a prompt row. */
export type PromptStatus = 0 | 1 | 2 | 9; // pending | approved | rejected | deleted

/** Accountability flavor, returned on each prompt. */
export type PromptAccountType = 0 | 1; // promotional | transactional

/** Categories the dialplan recognizes. */
export type PromptCategory =
  | 'welcome'
  | 'menu'
  | 'noinput'
  | 'wronginput'
  | 'thanks'
  | 'tts'
  | 'noagent';

/**
 * Categories the user can pick for upload / synthesize / filter. `tts` is
 * still a valid `PromptCategory` value the dialplan recognizes and that
 * legacy TTS prompts still carry — but we don't expose it as a manual
 * choice (TTS prompts have their own dedicated module).
 */
export const PROMPT_CATEGORIES: ReadonlyArray<PromptCategory> = [
  'welcome',
  'menu',
  'noinput',
  'wronginput',
  'thanks',
  'noagent',
];

export const CATEGORY_LABEL: Record<PromptCategory, string> = {
  welcome: 'Welcome',
  menu: 'Menu',
  noinput: 'No-input',
  wronginput: 'Wrong-input',
  thanks: 'Thanks',
  tts: 'TTS',
  noagent: 'No-agent',
};

/** Row from `/api/obd/prompts/{userId}` or `/api/obd/prompt/list/filter`. */
export interface PromptRow {
  promptId: number;
  fileName: string;
  promptName?: string;
  promptUrl?: string;
  promptCategory: PromptCategory | string;
  promptStatus: PromptStatus | number | string;
  accountType?: PromptAccountType | number;
  promptDuration?: number;
  userId?: number;
  username?: string;
  uploadTime?: string;
  approvalTime?: string;
  delete_date?: string;
  deletedBy?: string;
  remarks?: string;
}

/* ---------------- Upload ---------------- */

export interface PromptUploadInput {
  userId: number | string;
  /** Friendly file name without extension (saved as `fileName`). */
  fileName: string;
  promptCategory: PromptCategory;
  /** "wav" | "mp3". */
  fileType: 'wav' | 'mp3';
  /** The binary blob the user picked. */
  waveFile: File;
}

/* ---------------- TTS synthesis ---------------- */

/** Voice gender values the synthesize endpoint understands. */
export type TtsGender = 'MALE' | 'FEMALE';

/** Initial preview-stage request — runs the conversion and returns audio. */
export interface TtsConvertRequest {
  userId: number | string;
  gender: TtsGender;
  textScript: string;
  characterCount: number;
  language: string;
  filename: string;
  category: PromptCategory;
}

export type TtsConvertResponse =
  | {
      status: 'success';
      downloadUrl: string;
      promptDuration: number;
      characterCount: number;
      ttsId: number;
      message?: string;
    }
  | { status: 'fail'; msg: string };

/** Second-stage save — promotes a pending TTS row to a library prompt. */
export interface TtsFinalizeRequest {
  id: number;
  userId: number | string;
  /** Always 1 (active) at save time. */
  status: 1;
  downloadUrl: string;
  promptName: string;
  promptDuration: number;
  characterCount: number;
  category: PromptCategory;
}

/* ---------------- TTS history ---------------- */

/** Row from `/api/obd/tts/list/{userId}`. */
export interface TtsHistoryRow {
  id: number;
  language: string;
  gender: TtsGender | string;
  category: PromptCategory | string;
  filename: string;
  textScript: string;
  characterCount: number;
  reqDate?: string;
  status: number | string;
  userName?: string;
  deletedBy?: string;
  delete_date?: string;
  promptDuration?: number;
  /** Sometimes present after the convert step, sometimes filled by reading the
   *  associated prompt row. */
  downloadUrl?: string;
}

/* ---------------- Approve / reject ---------------- */

export interface ApprovePromptInput {
  promptId: number;
  remarks: string;
}

export interface RejectPromptInput {
  promptId: number;
  remarks: string;
}

/* ---------------- Filters ---------------- */

export interface PromptListFilterInput {
  userId: number | string;
  /** "-1" = all, otherwise a category to filter by (legacy semantics). */
  remarks: string;
}

/* ---------------- Bulk delete ---------------- */

export interface BulkDeletePromptInput {
  userId: number | string;
  promptIds: string; // comma-joined ids — legacy expects this exact shape
}

/* ---------------- Transcribe-to-Hindi (external service) ---------------- */

export interface TranscribeRequest {
  audioUrl: string;
  gender: TtsGender;
}

export interface TranscribeResponse {
  status: 'success' | 'fail';
  detectedLanguage?: string;
  transcript?: string;
  hindiText?: string;
  downloadUrl?: string;
  msg?: string;
}

/* ---------------- Helpers ---------------- */

export function promptStatusLabel(s: PromptStatus | number | string): string {
  const n = Number(s);
  if (n === 0) return 'Pending';
  if (n === 1) return 'Approved';
  if (n === 2) return 'Rejected';
  if (n === 9) return 'Deleted';
  return String(s);
}

export function promptStatusTone(
  s: PromptStatus | number | string,
): 'success' | 'warning' | 'danger' | 'muted' {
  const n = Number(s);
  if (n === 1) return 'success';
  if (n === 0) return 'warning';
  if (n === 2) return 'danger';
  return 'muted';
}
